import React, { useRef, useState } from "react";
import axios from "axios";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { host } from "../../../connections/feathers";
import { proccessProjectsCreatedBatchExcelService } from "../../../services/feathers-rest";

const ACTION_META = {
  create: { label: "Crear", color: "#2e7d32", background: "#e8f5e9" },
  update: { label: "Actualizar", color: "#1565c0", background: "#e3f2fd" },
  inactivate: { label: "Inactivar", color: "#ef6c00", background: "#fff3e0" },
  skip: { label: "Omitir", color: "#616161", background: "#f5f5f5" },
  unchanged: { label: "Sin cambios", color: "#455a64", background: "#eceff1" },
};

const APPLIED_META = {
  create: { label: "Creado", color: "#2e7d32", background: "#e8f5e9" },
  update: { label: "Actualizado", color: "#1565c0", background: "#e3f2fd" },
  inactivate: { label: "Inactivado", color: "#ef6c00", background: "#fff3e0" },
  skip: { label: "Omitido", color: "#616161", background: "#f5f5f5" },
  unchanged: { label: "Sin cambios", color: "#455a64", background: "#eceff1" },
};

const SUMMARY_LABELS = {
  create: { preview: "Nuevos", done: "Creados" },
  update: { preview: "Se actualizan", done: "Actualizados" },
  inactivate: { preview: "Se inactivan", done: "Inactivados" },
  skip: { preview: "Se omiten", done: "Omitidos" },
  unchanged: { preview: "Sin cambios", done: "Sin cambios" },
};

const emptySummary = {
  create: 0,
  update: 0,
  inactivate: 0,
  skip: 0,
  unchanged: 0,
};

const apiErrorMessage = (error) => {
  const data = error && error.response && error.response.data;
  return (
    (data && (data.message || data.error)) ||
    (error && error.message) ||
    "No se pudo procesar el archivo"
  );
};

const extrasForRow = (row) => {
  const extras = [];
  if (row.company_action === "create") extras.push("Constructora nueva");
  if (row.phases && row.phases.create) {
    extras.push(
      row.phases.create === 1
        ? "1 etapa nueva"
        : row.phases.create + " etapas nuevas"
    );
  }
  const listedNewTypes = (row.changes || []).filter(
    (change) => String(change.field || "").indexOf("type:") === 0 && change.from === "(no existe)"
  ).length;
  const newTypes = row.types && row.types.create ? row.types.create : 0;
  if (newTypes > listedNewTypes) {
    extras.push(
      newTypes === 1 ? "1 tipo nuevo" : newTypes + " tipos nuevos"
    );
  }
  return extras;
};

const ChangeBlock = ({ row }) => {
  const changes = row.changes || [];
  const extras = row.action === "create" ? [] : extrasForRow(row);
  const showReason =
    row.action === "skip" ||
    row.action === "create" ||
    row.action === "inactivate" ||
    row.action === "unchanged";

  return (
    <div>
      {showReason && row.reason ? (
        <div style={{ marginBottom: changes.length ? 8 : 0 }}>{row.reason}</div>
      ) : null}
      {row.manual_edit ? (
        <div style={{ color: "#bf360c", fontSize: 12, marginBottom: 8 }}>
          Está en edición manual: el Excel lo sobrescribe igual.
        </div>
      ) : null}
      {changes.map((change, index) => (
        <div key={change.field + "-" + index} style={{ marginBottom: 6 }}>
          <div style={{ color: "#607d8b", fontSize: 11, fontWeight: 600 }}>
            {change.label}
          </div>
          {change.from === "(no existe)" || change.from === "(vacío)" ? (
            <div>{change.to}</div>
          ) : (
            <div>
              <span style={{ color: "#c62828" }}>{change.from}</span>
              <span style={{ color: "#90a4ae", margin: "0 6px" }}>→</span>
              <span style={{ color: "#1565c0", fontWeight: 600 }}>{change.to}</span>
            </div>
          )}
        </div>
      ))}
      {extras.map((extra) => (
        <Chip
          key={extra}
          size="small"
          label={extra}
          style={{ marginRight: 6, marginTop: 4 }}
        />
      ))}
    </div>
  );
};

const ExcelImportDialog = ({ showNotification, refreshView }) => {
  const inputFile = useRef(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("pick");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const reset = () => {
    setStep("pick");
    setFileName("");
    setPreview(null);
    setError("");
    setFilter("all");
    setSearch("");
    setUploading(false);
    setSyncing(false);
    if (inputFile.current) inputFile.current.value = "";
  };

  const close = () => {
    if (uploading || syncing) return;
    if (step === "success" && refreshView) refreshView();
    setOpen(false);
    reset();
  };

  const openDialog = () => {
    reset();
    setOpen(true);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const extension = (file.name.split(".").pop() || "").toLowerCase();
    if (extension !== "csv") {
      setError("El archivo debe ser CSV (guarda el Excel como CSV).");
      setStep("pick");
      return;
    }

    setUploading(true);
    setError("");
    setFileName(file.name);
    setStep("uploading");

    try {
      const data = new FormData();
      const token = localStorage.getItem("feathers-jwt");
      data.append("excel_file", file);
      const response = await axios.post(`${host}/batch-projects-excel`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token,
        },
      });
      const payload = response.data || {};
      if (payload.message && String(payload.message).indexOf("no cargado") === 0) {
        throw new Error(payload.message);
      }
      setPreview(payload.preview || null);
      setStep("preview");
    } catch (err) {
      setError(apiErrorMessage(err));
      setStep("pick");
    } finally {
      setUploading(false);
      if (inputFile.current) inputFile.current.value = "";
    }
  };

  const confirmSync = async () => {
    setSyncing(true);
    setError("");
    try {
      const result = await proccessProjectsCreatedBatchExcelService.create({});
      if (result && result.preview) setPreview(result.preview);
      setStep("success");
      if (showNotification) showNotification("Sincronización completada");
      if (refreshView) refreshView();
    } catch (err) {
      setError(apiErrorMessage(err));
      if (showNotification) {
        showNotification("Error al sincronizar", "warning");
      }
    } finally {
      setSyncing(false);
    }
  };

  const summary = (preview && preview.summary) || emptySummary;
  const applied = step === "success";
  const meta = applied ? APPLIED_META : ACTION_META;
  const projects = (preview && preview.projects) || [];
  const filtered = projects.filter((row) => {
    if (filter !== "all" && row.action !== filter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      String(row.name || "").toLowerCase().indexOf(q) !== -1 ||
      String(row.company || "").toLowerCase().indexOf(q) !== -1 ||
      String(row.city || "").toLowerCase().indexOf(q) !== -1
    );
  });

  const title =
    step === "success" ? "Listo, ya quedó sincronizado" : "Cargar Excel";

  const summaryChips = ["create", "update", "inactivate", "skip", "unchanged"]
    .filter((key) => summary[key] > 0)
    .map((key) => {
      const labels = SUMMARY_LABELS[key];
      return {
        key: key,
        label: (applied ? labels.done : labels.preview) + " · " + summary[key],
      };
    });

  return (
    <span>
      <input
        accept=".csv,text/csv"
        style={{ display: "none" }}
        ref={inputFile}
        type="file"
        onChange={(e) => uploadFile(e.target.files[0])}
      />
      <Button color="primary" className="paddingHeight" onClick={openDialog}>
        Cargar Excel
      </Button>
      <Dialog
        open={open}
        onClose={close}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          {error ? (
            <div
              style={{
                background: "#ffebee",
                color: "#c62828",
                padding: "10px 12px",
                borderRadius: 4,
                marginBottom: 16,
              }}
            >
              {error}
            </div>
          ) : null}

          {step === "pick" || step === "uploading" ? (
            <div>
              <Typography variant="body1" paragraph>
                Sube el CSV. Antes de guardar te decimos qué proyecto se crea,
                se actualiza o se omite.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                disabled={uploading}
                onClick={() => inputFile.current && inputFile.current.click()}
              >
                {uploading ? "Revisando archivo…" : "Seleccionar CSV"}
              </Button>
              {uploading ? (
                <div style={{ display: "flex", alignItems: "center", marginTop: 16 }}>
                  <CircularProgress size={22} />
                  <span style={{ marginLeft: 12 }}>Comparando con lo que hay hoy…</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {(step === "preview" || step === "success") && preview ? (
            <div>
              {step === "success" ? (
                <div
                  style={{
                    background: "#e8f5e9",
                    color: "#1b5e20",
                    padding: "10px 12px",
                    borderRadius: 4,
                    marginBottom: 12,
                  }}
                >
                  Se aplicó el archivo {fileName || ""}.
                </div>
              ) : (
                <Typography variant="body2" color="textSecondary" paragraph>
                  {fileName} · {projects.length} proyecto
                  {projects.length === 1 ? "" : "s"}
                </Typography>
              )}

              <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 8 }}>
                {summaryChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    clickable
                    label={chip.label}
                    onClick={() => setFilter(chip.key)}
                    style={{
                      background: meta[chip.key].background,
                      color: meta[chip.key].color,
                      fontWeight: filter === chip.key ? 700 : 500,
                      marginRight: 8,
                      marginBottom: 8,
                    }}
                  />
                ))}
                <Chip
                  clickable
                  label={"Todos · " + projects.length}
                  onClick={() => setFilter("all")}
                  variant={filter === "all" ? "default" : "outlined"}
                  style={{ marginRight: 8, marginBottom: 8 }}
                />
              </div>

              {preview.error ? (
                <div
                  style={{
                    background: "#fff8e1",
                    color: "#f57f17",
                    padding: "10px 12px",
                    borderRadius: 4,
                    marginBottom: 12,
                  }}
                >
                  No se pudo armar todo el detalle: {preview.error}
                </div>
              ) : null}

              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginBottom: 12 }}
              >
                No se tocan fotos, zonas ni la descripción. Revisa cada fila y
                confirma.
              </Typography>

              <TextField
                size="small"
                variant="outlined"
                placeholder="Buscar por nombre o constructora"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                style={{ marginBottom: 12 }}
              />

              <div style={{ maxHeight: 440, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Proyecto</TableCell>
                      <TableCell>Qué va a pasar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((row, index) => {
                      const action = meta[row.action] || meta.skip;
                      return (
                        <TableRow
                          key={(row.ref_project_id || "row") + "-" + index}
                        >
                          <TableCell
                            style={{
                              verticalAlign: "top",
                              width: "34%",
                              borderLeft: "4px solid " + action.color,
                            }}
                          >
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>
                              {row.name || "(sin nombre)"}
                            </div>
                            <div style={{ color: "#607d8b", fontSize: 12 }}>
                              {[row.city, row.company].filter(Boolean).join(" · ")}
                            </div>
                            <Chip
                              size="small"
                              label={action.label}
                              style={{
                                marginTop: 8,
                                background: action.background,
                                color: action.color,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell style={{ verticalAlign: "top" }}>
                            <ChangeBlock row={row} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!filtered.length ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          No hay proyectos con esa búsqueda.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={uploading || syncing}>
            {step === "success" ? "Cerrar" : "Cancelar"}
          </Button>
          {step === "preview" ? (
            <Button
              onClick={() => inputFile.current && inputFile.current.click()}
              disabled={syncing}
            >
              Otro archivo
            </Button>
          ) : null}
          {step === "preview" ? (
            <Button
              color="primary"
              variant="contained"
              onClick={confirmSync}
              disabled={syncing || !preview}
            >
              {syncing ? (
                <span>
                  <CircularProgress size={16} style={{ marginRight: 8 }} />
                  Guardando…
                </span>
              ) : (
                "Confirmar"
              )}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </span>
  );
};

export default ExcelImportDialog;
